import React from 'react';
import styled from 'styled-components';
// import { lighten, darken } from 'polished';
import { palette } from 'styles/color';
import theme from 'styles/Theme';

type ButtonVariant = 'outlined' | 'contained' | 'text';
type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonPropsType = {
  children: React.ReactNode | string;
  variant?: ButtonVariant;
  color?: SementicColor;
  size?: ButtonSize;
  disabled?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * 버튼 컴포넌트
 * @param {React.ReactNode | string} children - 버튼 안에 들어갈 내용
 * @param {ButtonVariant} [variant='contained'] - 버튼 스타일 ('outlined' | 'contained' | 'text')
 * @param {SementicColor} [color='default'] - 버튼 색상
 * @param {ButtonSize} [size='medium'] - 버튼 크기 ('small' | 'medium' | 'large';)
 * @param {boolean} [disabled=false] - 비활성화 여부
 * @param {React.ReactNode} [startIcon] - 시작 아이콘
 * @param {React.ReactNode} [endIcon] - 끝 아이콘
 * @param {boolean} [fullWidth=false] - 가로 전체 사이즈 여부
 * @param {React.ButtonHTMLAttributes<HTMLButtonElement>} rest - HTLM Button Element의 기본 속성
 */
const Button: React.FC<ButtonPropsType> = ({
  children,
  variant = 'outlined',
  color = 'default',
  size = 'medium',
  disabled = false,
  startIcon,
  endIcon,
  fullWidth = false,
  ...rest
}) => {
  return (
    <StyledButton
      variant={variant}
      color={color}
      size={size}
      disabled={disabled}
      fullWidth={fullWidth}
      {...rest}>
      {startIcon && <IconWrapper>{startIcon}</IconWrapper>}
      <span>{children}</span>
      {endIcon && <IconWrapper>{endIcon}</IconWrapper>}
    </StyledButton>
  );
};

export default Button;

const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'fullWidth'
})<ButtonPropsType>`
  display: flex;
  align-items: center;
  justify-content: center;

  max-height: 38px;

  line-height: 100%;
  border-radius: 6px;
  border: 1px solid;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.1s;

  white-space: nowrap;

  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

  ${({ size }) => {
    switch (size) {
      // case 'small':
      //   return `
      //     padding: 0.25rem 0.5rem;
      //     font-size: 0.75rem;
      //   `;
      case 'medium':
        return `
          padding: 10.5px 12px;
          font-size: 14px;
        `;
      // case 'large':
      //   return `
      //     padding: 0.75rem 1.5rem;
      //     font-size: 1.25rem;
      //   `;
      default:
        return null;
    }
  }}

  ${({ variant = 'outlined', color = 'default' }) => {
    switch (variant) {
      case 'outlined':
        return `
          background: ${palette.white};
          border-color: ${palette.gray[200]};
          color: ${palette.gray[800]};

          &:hover {
            background: ${palette.gray[100]};
          }
          &:disabled {
            background: ${palette.gray[200]} !important;
            border-color: ${palette.gray[200]} !important;
            color: ${palette.gray[400]} !important;
            // cursor: not-allowed !important;
          }
          &:active {
            background: ${theme.colors[color]};
            border-color: '#bebebe';
            color: ${palette.white};
          }
        `;
      case 'contained':
        return `
            background: ${palette.white};
            color: ${palette.gray[800]};
            border: 1.2px solid transparent;
            box-shadow: 0.71px 0.71px 2.83px 0px #0000001A;
            box-shadow: 0px 0.71px 2.12px 0px #00000014;

            &:hover {
              background: ${palette.gray[100]};
            }
  
            &:active {
              background: ${palette.white};
              border-color: ${theme.colors[color]};
              color: ${theme.colors[color]};
            }

            &:disabled {
              background: ${palette.gray[200]} !important;
              border-color: ${palette.gray[200]} !important;
              color: ${palette.gray[400]} !important;
              cursor: not-allowed !important;
            }
          `;
      case 'text':
        return `
          background: ${palette.transparent};
          color: ${palette.black};
          border: none;

          &:active {
            color: ${theme.colors[color]};
          }
        `;

      default:
        return null;
    }
  }}
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  margin: 0 4px;

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;
